/**
 * A list of reported Issues — the reporter's own ("mine") or every Issue (the ADMIN
 * "queue"). Search, a status filter and, on the queue, an owner filter; server-side
 * search and count, cursor pagination, 20 a page.
 *
 * Seven columns at most (Calm first view): Reference, Title, Reported by, Owner, Priority,
 * Status, Reported. "Mine" drops Reported by and Owner.
 */

import { Link } from "@tanstack/react-router";
import { Camera, LifeBuoy } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type SupportIssueListParams, useSupportIssues } from "@/hooks/use-support-issues";
import {
  CATEGORY_LABELS,
  PRIORITY_LABELS,
  STATUS_LABELS,
  SUPPORT_ISSUE_STATUSES,
} from "@/lib/support-issue-labels";
import { IssueStatusBadge } from "./IssueStatusBadge";
import { useReportIssue } from "./ReportIssueProvider";

const PAGE_SIZE = 20;
const ANY_OWNER = "any";

type StatusFilter = SupportIssueListParams["status"];
type OwnerFilter = typeof ANY_OWNER | "me" | "unassigned";

function isStatusFilter(value: string): value is StatusFilter {
  return value === "active" || value === "all" || SUPPORT_ISSUE_STATUSES.some((s) => s === value);
}

function isOwnerFilter(value: string): value is OwnerFilter {
  return value === ANY_OWNER || value === "me" || value === "unassigned";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function IssueList({ scope }: { scope: "mine" | "queue" }) {
  const { openReportIssue } = useReportIssue();
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 300);
  const [status, setStatus] = useState<StatusFilter>("active");
  const [owner, setOwner] = useState<OwnerFilter>(ANY_OWNER);
  const [cursor, setCursor] = useState<{ cursor: string; direction: "next" | "prev" } | null>(null);
  const [page, setPage] = useState(1);

  const resetPaging = () => {
    setCursor(null);
    setPage(1);
  };

  const { data, isLoading, isError, error, refetch, isFetching } = useSupportIssues({
    scope,
    status,
    owner: scope === "queue" && owner !== ANY_OWNER ? owner : undefined,
    search: debounced.trim() || undefined,
    cursor: cursor?.cursor,
    direction: cursor?.direction,
  });

  const queue = scope === "queue";
  const headers = queue
    ? ["Reference", "Title", "Reported by", "Owner", "Priority", "Status", "Reported"]
    : ["Reference", "Title", "Priority", "Status", "Reported"];
  const filtered = debounced.trim() !== "" || status !== "active" || owner !== ANY_OWNER;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onValueChange={(v) => {
            setSearch(v);
            resetPaging();
          }}
          placeholder={queue ? "Search title, reference or reporter" : "Search your issues"}
          className="w-full sm:w-72"
        />
        <Select
          value={status}
          onValueChange={(v) => {
            if (!isStatusFilter(v)) return;
            setStatus(v);
            resetPaging();
          }}
        >
          <SelectTrigger className="w-40" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
            {SUPPORT_ISSUE_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {queue && (
          <Select
            value={owner}
            onValueChange={(v) => {
              if (!isOwnerFilter(v)) return;
              setOwner(v);
              resetPaging();
            }}
          >
            <SelectTrigger className="w-40" aria-label="Owner">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_OWNER}>Any owner</SelectItem>
              <SelectItem value="me">Assigned to me</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Issues couldn't be loaded."}
          onRetry={() => refetch()}
        />
      ) : !isLoading && data && data.total === 0 && !filtered ? (
        <EmptyState
          icon={LifeBuoy}
          title={queue ? "No open issues" : "You haven't reported anything yet"}
          description={
            queue
              ? "When someone reports an issue it lands here."
              : "Spotted something wrong? Report it and follow it here until it's fixed."
          }
          action={queue ? undefined : <Button onClick={openReportIssue}>Report an issue</Button>}
        />
      ) : (
        <>
          <DataTable
            headers={headers.map((label) => ({ label }))}
            loading={isLoading}
            empty={
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                No issues match these filters.
              </p>
            }
          >
            {data?.data.map((issue) => (
              <tr key={issue.id} className="border-b last:border-b-0 hover:bg-muted/40">
                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs">
                  <Link
                    to="/dashboard/issues/$id"
                    params={{ id: issue.id }}
                    className="text-primary hover:underline"
                  >
                    {issue.code}
                  </Link>
                </td>
                <td className="max-w-md px-4 py-3">
                  <Link
                    to="/dashboard/issues/$id"
                    params={{ id: issue.id }}
                    className="flex items-center gap-1.5 font-medium text-foreground hover:underline"
                  >
                    <span className="truncate">{issue.title}</span>
                    {issue.hasScreenshot && (
                      <Camera
                        className="size-3.5 shrink-0 text-muted-foreground"
                        aria-label="Has a screenshot"
                      />
                    )}
                  </Link>
                  <p className="truncate text-xs text-muted-foreground">
                    {[issue.category ? CATEGORY_LABELS[issue.category] : null, issue.route]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </td>
                {queue && (
                  <td className="px-4 py-3">
                    <p className="truncate">{issue.reporterName}</p>
                    <p className="truncate text-xs text-muted-foreground">{issue.reporterRole}</p>
                  </td>
                )}
                {queue && (
                  <td className="px-4 py-3 text-muted-foreground">
                    {issue.ownerName ?? "Unassigned"}
                  </td>
                )}
                <td className="px-4 py-3">{PRIORITY_LABELS[issue.priority]}</td>
                <td className="px-4 py-3">
                  <IssueStatusBadge status={issue.status} />
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                  {formatDate(issue.createdAt)}
                </td>
              </tr>
            ))}
          </DataTable>
          {data && (data.hasNext || data.hasPrev) && (
            <CursorPagination
              total={data.total}
              page={page}
              limit={PAGE_SIZE}
              hasNext={data.hasNext}
              hasPrev={data.hasPrev}
              loading={isFetching}
              itemLabel="issues"
              onNext={() => {
                if (!data.nextCursor) return;
                setCursor({ cursor: data.nextCursor, direction: "next" });
                setPage((p) => p + 1);
              }}
              onPrev={() => {
                if (!data.prevCursor) return;
                setCursor({ cursor: data.prevCursor, direction: "prev" });
                setPage((p) => Math.max(1, p - 1));
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
